// File: src/pages/TaxReport.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Button,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  Badge,
  Spinner,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { ImDownload } from "react-icons/im";
import { FaFilter } from "react-icons/fa";
import { MdCancel } from "react-icons/md";
import {
  BsFillArrowUpSquareFill,
  BsFillArrowDownSquareFill,
} from "react-icons/bs";
import { FaArrowAltCircleLeft, FaArrowAltCircleRight } from "react-icons/fa";
import jsPDF from "jspdf";
import "jspdf-autotable";
import Papa from "papaparse";
import * as XLSX from "xlsx";

// 🔗 All shared stuff lives under ../import/
import { _Post } from "../import/ApiEndPoint/ApiCalling";
import { Tax_Report } from "../import/ApiEndPoint/Api";
import SuperAdminUI from "../import/SuperAdminUI";
import { CheckPermission } from "../import/CheckPermission";

// ---- small helpers
const getUser = () => JSON.parse(localStorage.getItem("userData") || "{}");
const num2 = (v) => (Number.isFinite(+v) ? (+v).toFixed(2) : "");

// Table defaults
const DEFAULT_COLS = [
  {
    key: "totalTaxInput",
    header: "Total Tax Input",
    format: (r) => num2(r?.totalTaxInput),
  },
  {
    key: "totalTaxOut",
    header: "Total Tax Output",
    format: (r) => num2(r?.totalTaxOut),
  },
  {
    key: "BalanceTax",
    header: "BALANCE TAX",
    format: (r) => num2(r?.BalanceTax),
  },
];

// backwards-compat mapper for any old localStorage saved ag-grid defs
const mapLegacyCols = (arr = []) => {
  const keys = new Set(DEFAULT_COLS.map((c) => c.key));
  return arr
    .map((c) => {
      const key = c?.field;
      const header = c?.headerName;
      if (!key || !header || !keys.has(key)) return null;
      return {
        key,
        header,
        format:
          DEFAULT_COLS.find((d) => d.key === key)?.format || ((r) => r?.[key]),
      };
    })
    .filter(Boolean);
};

const injectOnce = () => {
  if (document.getElementById("tax-inline-css")) return;
  const s = document.createElement("style");
  s.id = "tax-inline-css";
  s.textContent = `
    .t-toolbar .count-pill{ background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px; }
    .t-card{ border-radius:14px; border:1px solid #e5e7eb; }
    .t-table-wrap{ width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff; }
    table.t-table{ width:100%; border-collapse:collapse; min-width:800px; }
    .t-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb; padding:8px 10px; position:sticky; top:0; z-index:1; text-align:center; white-space:nowrap;
    }
    .t-table tbody td{ padding:8px 10px; border-bottom:1px solid #f1f5f9; vertical-align:middle; text-align:center; }
    .t-chip{ display:inline-grid; place-items:center; width:26px; height:26px; border-radius:14px; cursor:pointer; background:#e2e8f0; }
    .t-small{ font-size:12px; color:#6b7280; }
    .t-del{ position:absolute; right:8px; top:4px; cursor:pointer; }
  `;
  document.head.appendChild(s);
};

export default function TaxReport() {
  useEffect(() => injectOnce(), []);
  const navigate = useNavigate();

  // ===== state
  const [loading, setLoading] = useState(true);
  const [masterShow, setMasterShow] = useState(false);
  const [permissions, setPermissions] = useState({});
  const [isDLopen, setIsDLopen] = useState(false);

  const [rows, setRows] = useState([]); // [{ totalTaxInput, totalTaxOut, BalanceTax }]
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // column chooser
  const [modalOpen, setModalOpen] = useState(false);
  const [allCols] = useState(DEFAULT_COLS);
  const [visibleCols, setVisibleCols] = useState(DEFAULT_COLS);
  const [selIdx, setSelIdx] = useState(-1); // index in visibleCols
  const [pendingAdd, setPendingAdd] = useState(new Set()); // keys to add from available

  // ===== init
  useEffect(() => {
    const u = getUser();
    setMasterShow(u?.rolename?.roleName === "MASTER");
    setPermissions(CheckPermission("TAX REPORT") || {});
    // visible columns from LS (legacy safe)
    try {
      const raw = JSON.parse(localStorage.getItem("TaxReportList") || "null");
      if (Array.isArray(raw) && raw.length && raw[0]?.field) {
        const mapped = mapLegacyCols(raw);
        if (mapped.length) setVisibleCols(mapped);
      }
    } catch {}
    // first fetch
    (async () => {
      try {
        await fetchData(u?._id, u?.database);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ===== API
  const fetchData = async (idOrPayload, db) => {
    setLoading(true);
    try {
      const res = await _Post(Tax_Report, db, idOrPayload);
      // API returns single object at res.Tax — make it a one-row table
      const one = res?.Tax ? [res.Tax] : [];
      setRows(one);
    } catch (e) {
      console.error("Tax_Report error:", e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // Master (SuperAdminUI)
  const handleParentSubmit = async (e) => {
    e.preventDefault();
    const raw = localStorage.getItem("SuperadminIdByMaster");
    if (!raw) return;
    const [id, db] = JSON.parse(raw).split(" ");
    await fetchData(id, db);
  };
  const handleDropdownChange = (selectedValue) => {
    localStorage.setItem("SuperadminIdByMaster", JSON.stringify(selectedValue));
  };

  // ===== filters
  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const t = search.trim().toLowerCase();
    return rows.filter((r) =>
      visibleCols
        .map((c) => String(c.format ? c.format(r) : (r?.[c.key] ?? "")))
        .join(" ")
        .toLowerCase()
        .includes(t),
    );
  }, [rows, search, visibleCols]);

  const onSubmitDate = async () => {
    const u = getUser();
    await fetchData({ startDate, endDate }, u?.database);
  };

  // ===== column chooser
  const availableCols = useMemo(
    () => allCols.filter((c) => !visibleCols.some((v) => v.key === c.key)),
    [allCols, visibleCols],
  );

  const addSelectedAvailable = () => {
    if (!pendingAdd.size) return;
    const toAdd = availableCols.filter((c) => pendingAdd.has(c.key));
    const next = [...visibleCols, ...toAdd];
    setVisibleCols(next);
    setPendingAdd(new Set());
  };
  const removeAtIndex = (i) => {
    const next = visibleCols.slice();
    next.splice(i, 1);
    setVisibleCols(next);
    setSelIdx(-1);
  };
  const moveUp = () => {
    if (selIdx <= 0) return;
    const next = visibleCols.slice();
    const [el] = next.splice(selIdx, 1);
    next.splice(selIdx - 1, 0, el);
    setVisibleCols(next);
    setSelIdx(selIdx - 1);
  };
  const moveDown = () => {
    if (selIdx < 0 || selIdx >= visibleCols.length - 1) return;
    const next = visibleCols.slice();
    const [el] = next.splice(selIdx, 1);
    next.splice(selIdx + 1, 0, el);
    setVisibleCols(next);
    setSelIdx(selIdx + 1);
  };
  const applyColumnChanges = () => {
    // save a legacy-compatible shape (headerName/field) to keep your current LS usage working
    const legacy = visibleCols.map((c) => ({
      headerName: c.header,
      field: c.key,
    }));
    localStorage.setItem("TaxReportList", JSON.stringify(legacy));
    setModalOpen(false);
  };

  // ===== downloads (use current filtered + visibleCols)
  const makeFlat = () =>
    filtered.map((row) => {
      const obj = {};
      visibleCols.forEach(
        (c) => (obj[c.header] = c.format ? c.format(row) : row?.[c.key]),
      );
      return obj;
    });

  const exportCSV = () => {
    const csv = Papa.unparse(makeFlat());
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Tax.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportXLSX = () => {
    const ws = XLSX.utils.json_to_sheet(makeFlat());
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, "Tax.xlsx");
  };

  const exportXLS = () => {
    const ws = XLSX.utils.json_to_sheet(makeFlat());
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, "Tax.xls");
  };

  const exportXML = () => {
    const data = makeFlat();
    const escape = (s = "") =>
      String(s)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    let xml = "<rows>\n";
    data.forEach((r) => {
      xml += "  <row>\n";
      Object.entries(r).forEach(([k, v]) => {
        xml += `    <${k.replace(/\s+/g, "_")}>${escape(v)}</${k.replace(
          /\s+/g,
          "_",
        )}>\n`;
      });
      xml += "  </row>\n";
    });
    xml += "</rows>";
    const blob = new Blob([xml], { type: "text/xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Tax.xml";
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = () => {
    const data = makeFlat();
    if (!data.length) return;
    const doc = new jsPDF("landscape", "mm", "a4");
    const head = [Object.keys(data[0])];
    const body = data.map((r) => Object.values(r));
    doc.text("Tax Report", 14, 14);
    doc.autoTable({ head, body, startY: 20 });
    doc.save("Tax.pdf");
  };

  // ===== UI
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  return (
    <>
      {/* Toolbar */}
      <Row className="t-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            className="t-card"
            style={{ padding: 8, background: "#f4f7f6" }}
          >
            <div
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Tax Report <span className="count-pill">{filtered.length}</span>
              </div>

              {/* SuperAdmin switcher */}
              {masterShow ? (
                <div style={{ flex: "1 1 260px", maxWidth: 360 }}>
                  <SuperAdminUI
                    onDropdownChange={handleDropdownChange}
                    onSubmit={handleParentSubmit}
                  />
                </div>
              ) : (
                <div style={{ flex: "1 1 120px" }} />
              )}

              {/* Search */}
              <div
                style={{
                  minWidth: 220,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <Input
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ background: "#fff" }}
                />
              </div>

              {/* Dates + submit */}
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <div>
                  <Label className="mb-0" style={{ fontSize: 12 }}>
                    Start Date
                  </Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={{ height: 32 }}
                  />
                </div>
                <div>
                  <Label className="mb-0" style={{ fontSize: 12 }}>
                    End Date
                  </Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    style={{ height: 32 }}
                  />
                </div>
                <Button
                  color="primary"
                  onClick={onSubmitDate}
                  style={{
                    height: 32,
                    fontWeight: 700,
                    textTransform: "uppercase",
                  }}
                >
                  Submit
                </Button>
              </div>

              {/* Column filter */}
              {permissions?.View && (
                <span title="Filter columns">
                  <FaFilter
                    style={{ cursor: "pointer" }}
                    size={20}
                    onClick={() => setModalOpen(true)}
                    color="rgb(8, 91, 245)"
                  />
                </span>
              )}

              {/* Download */}
              {permissions?.Download && (
                <div
                  onMouseEnter={() => setIsDLopen(true)}
                  onMouseLeave={() => setIsDLopen(false)}
                  style={{ position: "relative" }}
                >
                  <ImDownload
                    style={{ cursor: "pointer" }}
                    title="Download"
                    size={24}
                    color="rgb(8, 91, 245)"
                  />
                  {isDLopen && (
                    <div
                      style={{
                        position: "absolute",
                        right: 0,
                        top: 32,
                        zIndex: 5,
                        border: "1px solid rgb(8, 91, 245)",
                        backgroundColor: "white",
                        borderRadius: 10,
                        overflow: "hidden",
                        minWidth: 150,
                        boxShadow: "0 12px 24px rgba(0,0,0,.12)",
                      }}
                    >
                      <div
                        onClick={exportPDF}
                        style={{
                          padding: "10px 12px",
                          cursor: "pointer",
                          fontWeight: 600,
                        }}
                      >
                        .PDF
                      </div>
                      <div
                        onClick={exportCSV}
                        style={{
                          padding: "10px 12px",
                          cursor: "pointer",
                          fontWeight: 600,
                        }}
                      >
                        .CSV
                      </div>
                      <div
                        onClick={exportXLS}
                        style={{
                          padding: "10px 12px",
                          cursor: "pointer",
                          fontWeight: 600,
                        }}
                      >
                        .XLS
                      </div>
                      <div
                        onClick={exportXLSX}
                        style={{
                          padding: "10px 12px",
                          cursor: "pointer",
                          fontWeight: 600,
                        }}
                      >
                        .XLSX
                      </div>
                      <div
                        onClick={exportXML}
                        style={{
                          padding: "10px 12px",
                          cursor: "pointer",
                          fontWeight: 600,
                        }}
                      >
                        .XML
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div style={{ marginLeft: "auto" }}>
                <Button
                  outline
                  color="secondary"
                  style={{ height: 32 }}
                  onClick={() => navigate(-1)}
                >
                  Back
                </Button>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Table */}
      <Row>
        <Col sm="12">
          <Card className="t-card">
            <CardBody>
              <div className="t-table-wrap">
                <table className="t-table">
                  <thead>
                    <tr>
                      {visibleCols.map((c) => (
                        <th key={c.key}>{c.header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length ? (
                      filtered.map((r, i) => (
                        <tr key={i}>
                          {visibleCols.map((c) => (
                            <td key={c.key}>
                              {c.format ? c.format(r) : r?.[c.key]}
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={visibleCols.length}
                          style={{ color: "#6b7280", padding: 16 }}
                        >
                          No records found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Column chooser modal */}
      <Modal
        isOpen={modalOpen}
        toggle={() => setModalOpen((s) => !s)}
        style={{ maxWidth: 1050 }}
      >
        <ModalHeader toggle={() => setModalOpen((s) => !s)}>
          Change Fields
        </ModalHeader>
        <ModalBody>
          <Row>
            {/* Available */}
            <Col lg="4" md="4" sm="12">
              <h4>Available Columns</h4>
              <div
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  padding: 8,
                  maxHeight: 360,
                  overflow: "auto",
                }}
              >
                {availableCols.map((c) => (
                  <label
                    key={c.key}
                    className="d-flex align-items-center"
                    style={{ gap: 8, padding: "6px 8px", cursor: "pointer" }}
                  >
                    <input
                      type="checkbox"
                      checked={pendingAdd.has(c.key)}
                      onChange={(e) => {
                        const next = new Set(pendingAdd);
                        if (e.target.checked) next.add(c.key);
                        else next.delete(c.key);
                        setPendingAdd(next);
                      }}
                    />
                    <span>{c.header}</span>
                  </label>
                ))}
              </div>
            </Col>

            {/* arrows */}
            <Col
              lg="2"
              md="2"
              sm="12"
              className="d-flex flex-column align-items-center justify-content-center"
            >
              <div style={{ cursor: "pointer", margin: 8 }}>
                <FaArrowAltCircleRight
                  size={30}
                  onClick={addSelectedAvailable}
                />
              </div>
              <div style={{ cursor: "pointer", margin: 8 }}>
                <FaArrowAltCircleLeft
                  size={30}
                  onClick={() => {
                    if (selIdx >= 0) removeAtIndex(selIdx);
                  }}
                />
              </div>
            </Col>

            {/* Visible */}
            <Col lg="6" md="6" sm="12">
              <Row>
                <Col lg="8" md="8" sm="12">
                  <h4>Visible Columns</h4>
                  <div
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 8,
                      padding: 8,
                      maxHeight: 360,
                      overflow: "auto",
                    }}
                  >
                    {visibleCols.map((c, i) => (
                      <div
                        key={c.key}
                        onClick={() => setSelIdx(i)}
                        className="d-flex align-items-center"
                        style={{
                          gap: 8,
                          padding: "6px 8px",
                          cursor: "pointer",
                          background: selIdx === i ? "#e8f0fe" : "transparent",
                          borderRadius: 6,
                          position: "relative",
                          marginBottom: 6,
                        }}
                      >
                        <span
                          title="remove"
                          style={{ position: "absolute", right: 10 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            removeAtIndex(i);
                          }}
                        >
                          <MdCancel size={20} color="red" />
                        </span>
                        <span>{c.header}</span>
                      </div>
                    ))}
                  </div>
                </Col>
                <Col
                  lg="4"
                  md="4"
                  sm="12"
                  className="d-flex flex-column align-items-center justify-content-center"
                >
                  <BsFillArrowUpSquareFill
                    size={30}
                    className="mb-2"
                    onClick={moveUp}
                    style={{ cursor: "pointer" }}
                  />
                  <BsFillArrowDownSquareFill
                    size={30}
                    onClick={moveDown}
                    style={{ cursor: "pointer" }}
                  />
                </Col>
              </Row>
            </Col>
          </Row>

          <Row className="mt-3">
            <Col>
              <div className="d-flex justify-content-center">
                <Badge
                  color="primary"
                  style={{ cursor: "pointer" }}
                  onClick={applyColumnChanges}
                >
                  Submit
                </Badge>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>
    </>
  );
}
